/**
 * VechainMonitor Page
 *
 * Monitor VeChain accounts, VET/VTHO balances, VTHO generation, and economic nodes.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useVechainWallets, useVechainPrices } from '../hooks/useVechainWallet';
import { useVechainMonitorWallets } from '../hooks/useMonitorWallets';
import { isValidVechainAddress, vechainAPI, calculateVTHOGeneration } from '../services/vechainApi';
import { useToast } from '../components/Toast';
import { VechainHealthMetrics } from '../components/vechain/VechainHealthMetrics';
import { VetSupplyBreakdown } from '../components/vechain/VetSupplyBreakdown';
import {
  NetworkMonitorPageHeader,
  NetworkAddressInputForm,
  NetworkMonitorEmptyState,
  MonitorTabs,
} from '../components/monitor';
import { ResponsiveTable } from '../components/ui/ResponsiveTable';
import type { VeChainWalletData } from '../types/vechain.types';

const VET_COLORS = {
  primary: '#15BDFF',
  secondary: '#7B68EE',
  accent: '#00D4FF',
  gradient: 'linear-gradient(135deg, #15BDFF, #7B68EE)',
  lightGradient: 'linear-gradient(135deg, rgba(21, 189, 255, 0.15), rgba(123, 104, 238, 0.1))',
  success: '#10b981',
  warning: '#f59e0b',
  vtho: '#2ECC71',
};

type TabType = 'overview' | 'transactions' | 'vtho';

const s = {
  page: { padding: '0', maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', gap: '20px', flexWrap: 'wrap' as const },
  title: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleText: { fontSize: '24px', fontWeight: 700, margin: 0 },
  subtitle: { fontSize: '13px', color: 'var(--muted)', marginTop: '4px' },
  card: { background: 'rgba(0, 0, 0, 0.25)', backdropFilter: 'blur(20px)', borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.08)', padding: '20px', marginBottom: '20px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: (color: string) => ({ background: `${color}10`, border: `1px solid ${color}30`, borderRadius: '12px', padding: '16px', textAlign: 'center' as const }),
  statValue: { fontSize: '22px', fontWeight: 700 },
  statLabel: { fontSize: '11px', color: 'var(--muted)', marginTop: '4px', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  input: { width: '100%', padding: '12px 14px', background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace', outline: 'none' },
  button: { padding: '12px 20px', background: VET_COLORS.gradient, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' },
  buttonSecondary: { padding: '12px 20px', background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  tabContainer: { display: 'flex', gap: '4px', padding: '4px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '14px', width: 'fit-content', marginBottom: '24px' },
  tab: (isActive: boolean) => ({ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.3s ease', background: isActive ? VET_COLORS.gradient : 'transparent', color: isActive ? 'white' : 'var(--muted)' }),
  emptyState: { textAlign: 'center' as const, padding: '60px 20px' },
};

function NetworkStatsBanner({ vetPrice, vthoPrice, totalAccounts, isLoading }: { vetPrice: number; vthoPrice: number; totalAccounts: number; isLoading: boolean }) {
  const { t } = useTranslation();
  return (
    <div style={{ ...s.card, background: VET_COLORS.lightGradient, border: '1px solid rgba(21, 189, 255, 0.25)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', gap: '32px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>VET Price</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '28px', fontWeight: 700, color: VET_COLORS.primary }}>${vetPrice.toFixed(4)}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '4px' }}>VTHO Price</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '28px', fontWeight: 700, color: VET_COLORS.vtho }}>${vthoPrice.toFixed(6)}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '18px', fontWeight: 600 }}>{totalAccounts}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Accounts</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: isLoading ? VET_COLORS.warning : VET_COLORS.success, boxShadow: `0 0 10px ${isLoading ? VET_COLORS.warning : VET_COLORS.success}`, margin: '0 auto 4px' }} />
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{isLoading ? 'Syncing...' : 'Connected'}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatsOverview({ totalVetBalance, totalVetBalanceUsd, totalVthoBalance, totalVthoBalanceUsd, totalDailyVTHO, totalMonthlyVTHO, nodeCount, vthoPrice }: any) {
  const stats = [
    { label: 'VET Balance', value: `${totalVetBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })} VET`, subvalue: `$${totalVetBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: VET_COLORS.primary, icon: <Icons.Wallet size={16} /> },
    { label: 'VTHO Balance', value: `${totalVthoBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })} VTHO`, subvalue: `$${totalVthoBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}`, color: VET_COLORS.vtho, icon: <Icons.Zap size={16} /> },
    { label: 'Daily Generation', value: `${totalDailyVTHO.toLocaleString(undefined, { maximumFractionDigits: 2 })} VTHO`, subvalue: `$${(totalDailyVTHO * vthoPrice).toLocaleString(undefined, { maximumFractionDigits: 4 })}/day`, color: VET_COLORS.success, icon: <Icons.TrendingUp size={16} /> },
    { label: 'Monthly Generation', value: `${totalMonthlyVTHO.toLocaleString(undefined, { maximumFractionDigits: 0 })} VTHO`, subvalue: `${nodeCount} nodes`, color: VET_COLORS.accent, icon: <Icons.Calendar size={16} /> },
  ];
  return (
    <div style={s.statsGrid}>
      {stats.map((stat, idx) => (
        <div key={idx} style={s.statCard(stat.color)}>
          <div style={{ color: stat.color, marginBottom: '8px' }}>{stat.icon}</div>
          <div style={{ ...s.statValue, color: stat.color }}>{stat.value}</div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{stat.subvalue}</div>
          <div style={s.statLabel}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

function AccountCard({ wallet, onRemove, vthoPrice }: { wallet: VeChainWalletData; onRemove: () => void; vthoPrice: number }) {
  return (
    <div style={{ ...s.card, background: 'rgba(21, 189, 255, 0.08)', border: '1px solid rgba(21, 189, 255, 0.2)', marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: VET_COLORS.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icons.Vechain size={20} color="white" />
          </div>
          <div>
            <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, marginBottom: '2px' }}>{wallet.address.slice(0, 10)}...{wallet.address.slice(-6)}</div>
            {wallet.nodeType && <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: 'rgba(123, 104, 238, 0.15)', color: VET_COLORS.secondary, borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}><Icons.Award size={10} /> {wallet.nodeType}</div>}
          </div>
        </div>
        <button onClick={onRemove} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: 'none', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer' }}><Icons.Trash2 size={12} /></button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>VET</div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>{wallet.vetBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: '11px', color: VET_COLORS.success }}>${wallet.vetBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>VTHO</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: VET_COLORS.vtho }}>{wallet.vthoBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>${wallet.vthoBalanceUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>Daily VTHO</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: VET_COLORS.success }}>+{wallet.dailyVTHOGeneration.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>Monthly VTHO</div>
          <div style={{ fontSize: '16px', fontWeight: 600 }}>+{wallet.monthlyVTHOGeneration.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <NetworkMonitorEmptyState
      icon={<Icons.Vechain size={40} color="white" />}
      title="Welcome to VeChain Monitor"
      description="Track your VeChain addresses, monitor VET/VTHO balances, calculate VTHO generation, and view economic node status."
      colors={VET_COLORS}
    />
  );
}

export function VechainMonitor() {
  const toast = useToast();
  const { wallets: accounts, setWallets: setAccounts } = useVechainMonitorWallets();
  const [newAccountInput, setNewAccountInput] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { wallets, isLoading, error, refetch, vetPrice, vthoPrice, totalVetBalance, totalVetBalanceUsd, totalVthoBalance, totalVthoBalanceUsd, totalDailyVTHO, totalMonthlyVTHO, nodeCount } = useVechainWallets(accounts);

  const handleAddAccount = () => {
    const trimmedAddress = newAccountInput.trim();
    if (!trimmedAddress) { toast('Please enter an address', 'warning', 3000); return; }
    if (!isValidVechainAddress(trimmedAddress)) { toast('Invalid VeChain address format', 'error', 4000); return; }
    if (accounts.includes(trimmedAddress)) { toast('This address is already added', 'warning', 3000); return; }
    setAccounts([...accounts, trimmedAddress]);
    setNewAccountInput('');
    setShowAddAccount(false);
    toast('Address added successfully', 'success', 3000);
  };

  const handleRemoveAccount = (address: string) => {
    if (confirm(`Remove address?`)) {
      setAccounts(accounts.filter(a => a !== address));
      toast('Address removed', 'info', 3000);
    }
  };

  const isValidInput = newAccountInput.trim() && isValidVechainAddress(newAccountInput.trim());
  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Icons.BarChart size={16} /> },
    { id: 'vtho', label: 'VTHO Generation', icon: <Icons.Zap size={16} /> },
    { id: 'transactions', label: 'Transactions', icon: <Icons.ArrowRightLeft size={16} /> },
  ];

  const allTransactions = wallets.flatMap(w => w.recentTransactions.map(tx => ({ ...tx, walletAddress: w.address })));

  return (
    <div style={s.page}>
      <NetworkMonitorPageHeader
        icon={<Icons.Vechain size={36} color={VET_COLORS.primary} />}
        title="VeChain Monitor"
        subtitle="Monitor your VeChain addresses and VTHO generation"
        primaryAction={
          showAddAccount
            ? {
                label: 'Cancel',
                icon: <Icons.X size={16} />,
                onClick: () => setShowAddAccount(false),
                variant: 'secondary',
              }
            : {
                label: 'Add Address',
                icon: <Icons.Plus size={16} />,
                onClick: () => setShowAddAccount(true),
              }
        }
        refreshAction={
          accounts.length > 0
            ? {
                label: 'Refresh',
                icon: <Icons.RefreshCw size={16} />,
                onClick: () => refetch(),
              }
            : undefined
        }
        colors={VET_COLORS}
      />

      {showAddAccount && (
        <NetworkAddressInputForm
          value={newAccountInput}
          onChange={setNewAccountInput}
          onSubmit={handleAddAccount}
          submitLabel="Add"
          placeholder="Enter VeChain address (0x...)"
          isValid={newAccountInput.trim() ? Boolean(isValidInput) : null}
          colors={VET_COLORS}
        />
      )}

      <NetworkStatsBanner vetPrice={vetPrice} vthoPrice={vthoPrice} totalAccounts={accounts.length} isLoading={isLoading} />

      {/* V2.K.8 — VET + VTHO dual-token supply breakdown (cap VET fixe
          86,7 Md + VTHO uncapped staking-only + burn 100 % base fees
          Hayabusa). Fed by CoinGecko, refresh every 5 min. */}
      <VetSupplyBreakdown />

      {/* Blockchain Health Metrics */}
      <VechainHealthMetrics />

      {error && <div style={{ ...s.card, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}><div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444' }}><Icons.AlertTriangle size={20} /><span>Error: {(error as Error)?.message}</span></div></div>}

      {accounts.length > 0 ? (
        <>
          <StatsOverview totalVetBalance={totalVetBalance} totalVetBalanceUsd={totalVetBalanceUsd} totalVthoBalance={totalVthoBalance} totalVthoBalanceUsd={totalVthoBalanceUsd} totalDailyVTHO={totalDailyVTHO} totalMonthlyVTHO={totalMonthlyVTHO} nodeCount={nodeCount} vthoPrice={vthoPrice} />
          <MonitorTabs<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            colors={VET_COLORS}
            ariaLabel="VeChain monitor tabs"
          />

          {activeTab === 'overview' && (
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}><Icons.Wallet size={18} /> Accounts ({wallets.length})</h2>
              {isLoading ? <div style={{ textAlign: 'center', padding: '40px' }}><Icons.Activity size={32} style={{ opacity: 0.3 }} /></div> : wallets.map(wallet => <AccountCard key={wallet.address} wallet={wallet} onRemove={() => handleRemoveAccount(wallet.address)} vthoPrice={vthoPrice} />)}
            </div>
          )}

          {activeTab === 'vtho' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>VTHO Generation Calculator</h3>
              <ResponsiveTable<VeChainWalletData>
                data={wallets}
                keyExtractor={(w) => w.address}
                columns={[
                  {
                    key: 'address',
                    header: 'Address',
                    isTitle: true,
                    render: (w) => (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {w.address.slice(0, 10)}…
                      </span>
                    ),
                  },
                  {
                    key: 'vet',
                    header: 'VET Balance',
                    align: 'right',
                    render: (w) => `${w.vetBalance.toLocaleString()} VET`,
                  },
                  {
                    key: 'dailyVTHO',
                    header: 'Daily VTHO',
                    align: 'right',
                    render: (w) => (
                      <span style={{ color: VET_COLORS.vtho }}>
                        +{calculateVTHOGeneration(w.vetBalance).dailyVTHO.toFixed(2)}
                      </span>
                    ),
                  },
                  {
                    key: 'monthlyVTHO',
                    header: 'Monthly VTHO',
                    align: 'right',
                    render: (w) => (
                      <span style={{ color: VET_COLORS.success }}>
                        +{calculateVTHOGeneration(w.vetBalance).monthlyVTHO.toFixed(0)}
                      </span>
                    ),
                  },
                  {
                    key: 'yearlyVTHO',
                    header: 'Yearly VTHO',
                    align: 'right',
                    render: (w) => (
                      <span style={{ fontWeight: 600 }}>
                        +{calculateVTHOGeneration(w.vetBalance).yearlyVTHO.toFixed(0)}
                      </span>
                    ),
                  },
                ]}
              />
              <p style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '12px' }}>VTHO generation rate: 0.000432 VTHO per VET per day</p>
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Recent Transactions</h3>
              <ResponsiveTable
                data={allTransactions.slice(0, 50)}
                keyExtractor={(tx, idx) => `${tx.id}-${idx}`}
                emptyState={
                  <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                    No transactions found
                  </div>
                }
                columns={[
                  {
                    key: 'id',
                    header: 'TX ID',
                    isTitle: true,
                    render: (tx) => (
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: 'var(--muted)' }}>
                        {tx.id.slice(0, 16)}…
                      </span>
                    ),
                  },
                  {
                    key: 'clauses',
                    header: 'Clauses',
                    align: 'center',
                    render: (tx) => tx.clauses?.length || 0,
                  },
                  {
                    key: 'gas',
                    header: 'Gas',
                    align: 'right',
                    render: (tx) => tx.gas?.toLocaleString(),
                  },
                ]}
              />
            </div>
          )}
        </>
      ) : <EmptyState />}

      <div style={{ marginTop: '30px', padding: '16px 20px', background: 'rgba(21, 189, 255, 0.08)', borderRadius: '12px', border: '1px solid rgba(21, 189, 255, 0.15)', fontSize: '12px', color: 'var(--muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}><Icons.Info size={14} style={{ color: VET_COLORS.primary }} /><strong style={{ color: 'var(--text)' }}>About VeChain Monitor</strong></div>
        <p style={{ lineHeight: 1.6 }}>This page uses the VeChain Thor public API to fetch account data. VTHO is generated automatically at 0.000432 VTHO per VET per day.</p>
      </div>
    </div>
  );
}
